import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

const Blog = () => {
  // Schema.org Blog
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Croissance Consulting — Boulangerie & Pâtisserie",
    "description":
      "Articles, conseils et retours terrain pour artisans boulangers et pâtissiers : marge, prix, invendus, organisation.",
    "url": "https://croissanceconsulting.net/blog",
    "blogPost": blogPosts.map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://croissanceconsulting.net/blog/${post.slug}`,
      "datePublished": post.dateISO,
      "author": { "@type": "Person", "name": post.author },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Blog boulangerie & pâtisserie — conseils marge, prix, organisation | Croissance Consulting</title>
        <meta
          name="description"
          content="Articles et conseils pour artisans boulangers et pâtissiers : marge, prix, invendus, organisation. Des analyses concrètes et terrain pour reprendre la main sur votre rentabilité."
        />
        <link rel="canonical" href="https://croissanceconsulting.net/blog" />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]" data-testid="blog-list-page">
        {/* Hero */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins mb-6">
              Le blog
            </h1>
            <p className="text-lg sm:text-xl text-[#2D2D2D] leading-relaxed max-w-2xl mx-auto">
              Conseils, analyses et retours terrain pour artisans boulangers et pâtissiers : marge, prix, invendus, organisation.
            </p>
          </div>
        </section>

        {/* Articles list */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 pb-24">
          <div className="container mx-auto max-w-5xl">
            {blogPosts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg text-[#2D2D2D]">
                  Les premiers articles arrivent prochainement.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {blogPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#FBF7EF]">
                      <img
                        src={post.image}
                        alt={post.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col flex-grow">
                      <div className="flex items-center gap-4 text-xs text-[#6B7A3A] mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {post.dateFormatted}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {post.readTime} de lecture
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#4A3A2A] font-poppins mb-3 group-hover:text-[#B65C3A] transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-[#2D2D2D] leading-relaxed mb-6 flex-grow">
                        {post.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 text-[#B65C3A] font-medium mt-auto">
                        Lire l'article
                        <ArrowRight
                          size={18}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
